/**
 * Cashflow Chart Components
 * Three visualization styles for cashflow data:
 * 1. Bar Chart - Inflows (up) and Outflows (down) by date
 * 2. Waterfall Chart - Running balance over time (legacy, kept for rollback)
 * 3. Combo Chart - Daily net changes (bars) + Running balance line
 */

import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { Cashflow, ManualCashflow } from '../../types/cashflow';

interface ChartProps {
  cashflows: Cashflow[];
  currency: string;
  title?: string;
  manualCashflows?: ManualCashflow[];
  startingBalance?: number;
  height?: number;
}

// Helper to format large numbers with sufficient precision
const formatAmount = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue >= 1000000000) {
    return (value / 1000000000).toFixed(2) + 'B';
  }
  if (absValue >= 1000000) {
    return (value / 1000000).toFixed(2) + 'M';
  }
  if (absValue >= 1000) {
    return (value / 1000).toFixed(2) + 'K';
  }
  return value.toFixed(2);
};

// Helper to format date for display
// Parses YYYY-MM-DD without timezone issues by extracting components manually
const formatDateShort = (dateStr: string): string => {
  // Parse YYYY-MM-DD format manually to avoid UTC interpretation
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1; // JS months are 0-indexed
    const day = parseInt(match[3], 10);
    const date = new Date(year, month, day); // Local timezone
    return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
  }
  // Fallback for other formats
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
};

// Helper to parse DD-MM-YYYY or YYYY-MM-DD to sortable YYYY-MM-DD string (for manual cashflows)
const parseDDMMYYYY = (dateStr: string): string | null => {
  // Already YYYY-MM-DD
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) return dateStr;
  // DD-MM-YYYY → YYYY-MM-DD
  const match = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!match) return null;
  return `${match[3]}-${match[2]}-${match[1]}`;
};

// Helper to format DD-MM-YYYY to short display
const formatDDMMYYYYShort = (dateStr: string): string => {
  const isoDate = parseDDMMYYYY(dateStr);
  if (isoDate) {
    return formatDateShort(isoDate);
  }
  return dateStr;
};

// Helper to group manual cashflows by date
const groupManualByDate = (cashflows: ManualCashflow[], currency: string) => {
  const byDate: Record<string, { inbound: number; outbound: number }> = {};

  for (const cf of cashflows) {
    if (cf.currency !== currency) continue;

    if (!byDate[cf.expectedDate]) {
      byDate[cf.expectedDate] = { inbound: 0, outbound: 0 };
    }

    if (cf.flowDirection === 'inbound') {
      byDate[cf.expectedDate].inbound += cf.amount;
    } else {
      byDate[cf.expectedDate].outbound += cf.amount;
    }
  }

  return byDate;
};

// Helper to group cashflows by date
const groupByDate = (cashflows: Cashflow[], currency: string) => {
  const byDate: Record<string, { inbound: number; outbound: number; trades: Cashflow[] }> = {};

  for (const cf of cashflows) {
    if (cf.currency !== currency) continue;

    if (!byDate[cf.expectedDate]) {
      byDate[cf.expectedDate] = { inbound: 0, outbound: 0, trades: [] };
    }

    if (cf.flowDirection === 'inbound') {
      byDate[cf.expectedDate].inbound += cf.amount;
    } else {
      byDate[cf.expectedDate].outbound += cf.amount;
    }
    byDate[cf.expectedDate].trades.push(cf);
  }

  // Sort by date
  const sorted = Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b));
  return sorted;
};

/**
 * Bar Chart - Shows inflows (up, green) and outflows (down, red) by date
 * Manual cashflows shown as grey bars
 */
export const CashflowBarChart: React.FC<ChartProps> = ({ cashflows, currency, title, manualCashflows = [], height }) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    const grouped = groupByDate(cashflows, currency);
    const manualGrouped = groupManualByDate(manualCashflows, currency);

    // Combine all dates from both trade and manual cashflows
    const allDates = new Set<string>();
    grouped.forEach(([date]) => allDates.add(date));
    Object.keys(manualGrouped).forEach(date => allDates.add(date));

    // Sort dates
    const sortedDates = Array.from(allDates).sort();

    // Build data arrays aligned to all dates
    const dates: string[] = [];
    const inflows: number[] = [];
    const outflows: number[] = [];
    const manualFlows: number[] = [];

    sortedDates.forEach(date => {
      dates.push(formatDateShort(date));

      // Trade data
      const tradeData = grouped.find(([d]) => d === date);
      inflows.push(tradeData ? tradeData[1].inbound : 0);
      outflows.push(tradeData ? -tradeData[1].outbound : 0);

      // Manual data - net shown as grey bar
      const manual = manualGrouped[date];
      if (manual) {
        const netManual = manual.inbound - manual.outbound;
        manualFlows.push(netManual);
      } else {
        manualFlows.push(0);
      }
    });

    const hasManualData = manualFlows.some(v => v !== 0);
    return { dates, inflows, outflows, manualFlows, hasManualData };
  }, [cashflows, currency, manualCashflows]);

  const legendData = [
    t('cashflow.summary.inbound', 'Inbound'),
    t('cashflow.summary.outbound', 'Outbound'),
  ];
  if (chartData.hasManualData) {
    legendData.push(t('cashflow.manual.title', 'Manual'));
  }

  const option = {
    title: {
      text: title || `${t('cashflow.charts.barChart', 'Daily Inflows & Outflows')} (${currency})`,
      left: 'center',
      textStyle: { color: '#e0e0e0', fontSize: 14 },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const date = params[0]?.axisValue || '';
        let html = `<strong>${date}</strong><br/>`;
        params.forEach((p: any) => {
          if (p.value === 0) return;
          const value = Math.abs(p.value);
          let color = '#888';
          if (p.seriesName === t('cashflow.summary.inbound', 'Inbound')) color = '#4CAF50';
          else if (p.seriesName === t('cashflow.summary.outbound', 'Outbound')) color = '#ff6b6b';
          html += `<span style="color:${color}">${p.seriesName}: ${p.value >= 0 ? '+' : ''}${formatAmount(p.value)}</span><br/>`;
        });
        return html;
      },
    },
    legend: {
      show: false,
    },
    grid: {
      left: '12%',
      right: '5%',
      top: '15%',
      bottom: '15%',
    },
    xAxis: {
      type: 'category',
      data: chartData.dates,
      axisLabel: { color: '#b3b3b3', rotate: 45 },
      axisLine: { lineStyle: { color: '#444' } },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#b3b3b3',
        formatter: (value: number) => formatAmount(value),
      },
      axisLine: { lineStyle: { color: '#444' } },
      splitLine: { lineStyle: { color: '#333' } },
    },
    series: [
      {
        name: t('cashflow.summary.inbound', 'Inbound'),
        type: 'bar',
        stack: 'trade',
        data: chartData.inflows,
        itemStyle: { color: '#4CAF50' },
        emphasis: { itemStyle: { color: '#66BB6A' } },
      },
      {
        name: t('cashflow.summary.outbound', 'Outbound'),
        type: 'bar',
        stack: 'trade',
        data: chartData.outflows,
        itemStyle: { color: '#ff6b6b' },
        emphasis: { itemStyle: { color: '#ff8a8a' } },
      },
      ...(chartData.hasManualData ? [{
        name: t('cashflow.manual.title', 'Manual'),
        type: 'bar',
        data: chartData.manualFlows,
        itemStyle: { color: '#888888' },
        emphasis: { itemStyle: { color: '#999999' } },
        barGap: '10%',
      }] : []),
    ],
  };

  if (chartData.dates.length === 0) {
    return (
      <div style={{
        height: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
        backgroundColor: '#1e1e1e',
        borderRadius: 8,
      }}>
        {t('cashflow.messages.noData', 'No data available')} ({currency})
      </div>
    );
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: height ?? 300 }}
      opts={{ renderer: 'canvas' }}
      notMerge={true}
    />
  );
};

/**
 * Waterfall Chart - Shows running balance over time
 * Uses custom series with renderItem for precise bar positioning
 * Supports starting balance and manual cashflows
 */
export const CashflowWaterfallChart: React.FC<ChartProps> = ({ cashflows, currency, title, manualCashflows = [], startingBalance = 0, height }) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    const grouped = groupByDate(cashflows, currency);
    const manualGrouped = groupManualByDate(manualCashflows, currency);

    // Combine all dates from both trade and manual cashflows
    const allDates = new Set<string>();
    grouped.forEach(([date]) => allDates.add(date));
    Object.keys(manualGrouped).forEach(date => allDates.add(date));

    // Sort dates
    const sortedDates = Array.from(allDates).sort();

    const dates: string[] = [];
    const netChanges: number[] = [];
    const barTypes: Array<'opening' | 'daily' | 'final'> = [];

    // Add opening balance if provided
    if (startingBalance !== 0) {
      dates.push(t('cashflow.charts.openingBalance', 'Opening'));
      netChanges.push(startingBalance);
      barTypes.push('opening');
    }

    sortedDates.forEach(date => {
      // Trade data
      const tradeData = grouped.find(([d]) => d === date);
      const tradeNet = tradeData ? tradeData[1].inbound - tradeData[1].outbound : 0;

      // Manual data
      const manual = manualGrouped[date];
      const manualNet = manual ? manual.inbound - manual.outbound : 0;

      // Combined net for the day (trade + manual)
      const totalNet = tradeNet + manualNet;

      if (totalNet !== 0) {
        dates.push(formatDateShort(date));
        netChanges.push(totalNet);
        barTypes.push('daily'); // All daily bars are the same type - colored by +/-
      }
    });

    // Calculate running totals (balance AFTER each day's change)
    let runningTotal = 0;
    const runningTotals: number[] = [];
    netChanges.forEach((net, index) => {
      if (barTypes[index] === 'opening') {
        runningTotal = net; // Opening balance sets the starting point
      } else {
        runningTotal += net;
      }
      runningTotals.push(runningTotal);
    });

    // Add final balance bar
    dates.push(t('cashflow.charts.endBalance', 'End'));
    netChanges.push(runningTotal); // Final total value
    runningTotals.push(runningTotal);
    barTypes.push('final');

    return { dates, netChanges, runningTotals, finalTotal: runningTotal, barTypes };
  }, [cashflows, currency, t, manualCashflows, startingBalance]);

  // Build waterfall bar data with start/end positions
  const waterfallBars = useMemo(() => {
    const bars: Array<{
      index: number;
      start: number;
      end: number;
      value: number;
      isPositive: boolean;
      barType: 'opening' | 'daily' | 'final';
    }> = [];

    let prevTotal = 0;
    chartData.netChanges.forEach((change, index) => {
      const barType = chartData.barTypes[index];

      if (barType === 'final') {
        // Final bar: from 0 to final total
        bars.push({
          index,
          start: 0,
          end: change,
          value: change,
          isPositive: change >= 0,
          barType,
        });
      } else if (barType === 'opening') {
        // Opening balance bar: from 0 to opening balance
        bars.push({
          index,
          start: 0,
          end: change,
          value: change,
          isPositive: change >= 0,
          barType,
        });
        prevTotal = change;
      } else {
        // Daily bar: from previous total to new total (combines trade + manual)
        const newTotal = prevTotal + change;
        bars.push({
          index,
          start: prevTotal,
          end: newTotal,
          value: change,
          isPositive: change >= 0,
          barType,
        });
        prevTotal = newTotal;
      }
    });

    return bars;
  }, [chartData]);

  // Get color for bar based on type
  const getBarColor = (bar: typeof waterfallBars[0]): string => {
    switch (bar.barType) {
      case 'opening':
        return '#007bff'; // Blue for opening balance
      case 'final':
        return bar.isPositive ? '#4CAF50' : '#ff6b6b';
      default:
        // Daily bars: green for positive, red for negative
        return bar.isPositive ? '#4CAF50' : '#ff6b6b';
    }
  };

  // Custom render function for waterfall bars
  const renderItem = (params: any, api: any) => {
    const index = api.value(0);
    const bar = waterfallBars[index];
    if (!bar) return null;

    const categoryIndex = index;
    const start = api.coord([categoryIndex, bar.start]);
    const end = api.coord([categoryIndex, bar.end]);

    const rectHeight = Math.abs(end[1] - start[1]);
    const rectY = Math.min(start[1], end[1]);

    const barWidth = api.size([1, 0])[0] * 0.6;

    return {
      type: 'rect',
      shape: {
        x: start[0] - barWidth / 2,
        y: rectY,
        width: barWidth,
        height: rectHeight,
      },
      style: api.style({
        fill: getBarColor(bar),
      }),
    };
  };

  // Data for custom series: [index, start, end, value, isPositive]
  const customData = waterfallBars.map((bar) => [
    bar.index,
    bar.start,
    bar.end,
    bar.value,
    bar.isPositive ? 1 : 0,
  ]);

  const option = {
    title: {
      text: title || `${t('cashflow.charts.waterfall', 'Running Balance')} (${currency})`,
      left: 'center',
      textStyle: { color: '#e0e0e0', fontSize: 14 },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const date = params[0]?.axisValue || '';
        const index = chartData.dates.indexOf(date);
        if (index === -1) return '';

        const bar = waterfallBars[index];
        if (!bar) return '';

        const color = getBarColor(bar);

        if (bar.barType === 'final') {
          return `<strong>${date}</strong><br/>
                  <span style="color:${color}">
                    Total: ${bar.value >= 0 ? '+' : ''}${formatAmount(bar.value)}
                  </span>`;
        }

        if (bar.barType === 'opening') {
          return `<strong>${date}</strong><br/>
                  <span style="color:${color}">
                    ${t('cashflow.startingBalances.balance', 'Balance')}: ${formatAmount(bar.value)}
                  </span>`;
        }

        return `<strong>${date}</strong><br/>
                <span style="color:${color}">
                  ${t('cashflow.summary.net', 'Net')}: ${bar.value >= 0 ? '+' : ''}${formatAmount(bar.value)}
                </span><br/>
                <span style="color:#888">${t('cashflow.charts.runningBalance', 'Running')}: ${formatAmount(bar.end)}</span>`;
      },
    },
    legend: {
      show: false,
    },
    grid: {
      left: '12%',
      right: '5%',
      top: '15%',
      bottom: '15%',
    },
    xAxis: {
      type: 'category',
      data: chartData.dates,
      axisLabel: { color: '#b3b3b3', rotate: 45 },
      axisLine: { lineStyle: { color: '#444' } },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#b3b3b3',
        formatter: (value: number) => formatAmount(value),
      },
      axisLine: { lineStyle: { color: '#444' } },
      splitLine: { lineStyle: { color: '#333' } },
    },
    series: [
      {
        type: 'custom',
        renderItem: renderItem,
        encode: {
          x: 0,
          y: [1, 2],
        },
        data: customData,
      },
      // Hidden series for legend
      {
        name: t('cashflow.summary.inbound', 'Inbound'),
        type: 'bar',
        itemStyle: { color: '#4CAF50' },
        data: [],
      },
      {
        name: t('cashflow.summary.outbound', 'Outbound'),
        type: 'bar',
        itemStyle: { color: '#ff6b6b' },
        data: [],
      },
      ...(startingBalance !== 0 ? [{
        name: t('cashflow.charts.openingBalance', 'Opening'),
        type: 'bar',
        itemStyle: { color: '#007bff' },
        data: [],
      }] : []),
    ],
  };

  if (chartData.dates.length <= 1) {
    return (
      <div style={{
        height: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
        backgroundColor: '#1e1e1e',
        borderRadius: 8,
      }}>
        {t('cashflow.messages.noData', 'No data available')} ({currency})
      </div>
    );
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: height ?? 300 }}
      opts={{ renderer: 'canvas' }}
      notMerge={true}
    />
  );
};

/**
 * Combo Chart - Daily net changes (bars) + Running balance line
 * Best of both worlds: see daily impact AND cumulative trend
 */
export const CashflowComboChart: React.FC<ChartProps> = ({ cashflows, currency, title, manualCashflows = [], startingBalance = 0, height }) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    const grouped = groupByDate(cashflows, currency);
    const manualGrouped = groupManualByDate(manualCashflows, currency);

    // Combine all dates from both trade and manual cashflows
    const allDates = new Set<string>();
    grouped.forEach(([date]) => allDates.add(date));
    Object.keys(manualGrouped).forEach(date => allDates.add(date));

    // Sort dates
    const sortedDates = Array.from(allDates).sort();

    const dates: string[] = [];
    const netChanges: number[] = [];
    const runningBalances: number[] = [];

    // Add starting balance point before first cashflow
    dates.push(t('cashflow.charts.openingBalance', 'Opening'));
    netChanges.push(0); // No change on the starting point
    runningBalances.push(startingBalance);

    let runningTotal = startingBalance;

    sortedDates.forEach(date => {
      // Trade data
      const tradeData = grouped.find(([d]) => d === date);
      const tradeNet = tradeData ? tradeData[1].inbound - tradeData[1].outbound : 0;

      // Manual data
      const manual = manualGrouped[date];
      const manualNet = manual ? manual.inbound - manual.outbound : 0;

      // Combined net for the day
      const totalNet = tradeNet + manualNet;

      dates.push(formatDateShort(date));
      netChanges.push(totalNet);
      runningTotal += totalNet;
      runningBalances.push(runningTotal);
    });

    return { dates, netChanges, runningBalances, startingBalance };
  }, [cashflows, currency, manualCashflows, startingBalance, t]);

  const option = {
    title: {
      text: title || `${t('cashflow.charts.combo', 'Daily Changes & Running Balance')} (${currency})`,
      left: 'center',
      textStyle: { color: '#e0e0e0', fontSize: 14 },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const date = params[0]?.axisValue || '';
        const index = chartData.dates.indexOf(date);
        if (index === -1) return '';

        const netChange = chartData.netChanges[index];
        const balance = chartData.runningBalances[index];
        const netColor = netChange >= 0 ? '#4CAF50' : '#ff6b6b';

        return `<strong>${date}</strong><br/>
                <span style="color:${netColor}">
                  ${t('cashflow.summary.net', 'Net')}: ${netChange >= 0 ? '+' : ''}${formatAmount(netChange)}
                </span><br/>
                <span style="color:#64B5F6">
                  ${t('cashflow.charts.runningBalance', 'Balance')}: ${formatAmount(balance)}
                </span>`;
      },
    },
    legend: {
      show: false,
    },
    grid: {
      left: '12%',
      right: '5%',
      top: '15%',
      bottom: '15%',
    },
    xAxis: {
      type: 'category',
      data: chartData.dates,
      axisLabel: { color: '#b3b3b3', rotate: 45 },
      axisLine: { lineStyle: { color: '#444' } },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#b3b3b3',
        formatter: (value: number) => formatAmount(value),
      },
      axisLine: { lineStyle: { color: '#444' } },
      splitLine: { lineStyle: { color: '#333' } },
    },
    series: [
      {
        name: t('cashflow.charts.dailyNet', 'Daily Net'),
        type: 'bar',
        data: chartData.netChanges.map(v => ({
          value: v,
          itemStyle: { color: v >= 0 ? '#4CAF50' : '#ff6b6b' },
        })),
        emphasis: {
          itemStyle: { opacity: 0.8 },
        },
      },
      {
        name: t('cashflow.charts.runningBalance', 'Balance'),
        type: 'line',
        data: chartData.runningBalances,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          color: '#64B5F6',
          width: 2,
        },
        itemStyle: {
          color: '#64B5F6',
        },
      },
    ],
  };

  if (chartData.dates.length === 0) {
    return (
      <div style={{
        height: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
        backgroundColor: '#1e1e1e',
        borderRadius: 8,
      }}>
        {t('cashflow.messages.noData', 'No data available')} ({currency})
      </div>
    );
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: height ?? 300 }}
      opts={{ renderer: 'canvas' }}
      notMerge={true}
    />
  );
};

/**
 * Sankey Chart - Shows flow routing: Counterparty (inbound) → Bank Account → Counterparty (outbound)
 * Visualises which counterparties send money through which bank accounts to which outbound counterparties
 */
export const CashflowSankeyChart: React.FC<ChartProps> = ({ cashflows, currency, title, manualCashflows = [], height }) => {
  const { t } = useTranslation();

  const sankeyData = useMemo(() => {
    // Filter to this currency
    const currencyCashflows = cashflows.filter(cf => cf.currency === currency);
    const currencyManual = (manualCashflows || []).filter(cf => cf.currency === currency);

    // Aggregate inbound flows: counterparty → bank account
    const inboundFlows: Record<string, Record<string, number>> = {}; // {counterparty: {bankAccount: amount}}
    // Aggregate outbound flows: bank account → counterparty
    const outboundFlows: Record<string, Record<string, number>> = {}; // {bankAccount: {counterparty: amount}}

    const getBankLabel = (cf: Cashflow | ManualCashflow) => {
      if (cf.bankAccount?.bankName && cf.bankAccount?.accountNumber) {
        const num = cf.bankAccount.accountNumber;
        const masked = num.length > 4 ? '****' + num.slice(-4) : num;
        return `${cf.bankAccount.bankName} ${masked}`;
      }
      return t('cashflow.sankey.unassigned', 'Unassigned');
    };

    for (const cf of currencyCashflows) {
      const bank = getBankLabel(cf);
      const counterparty = cf.counterpartyName || t('cashflow.sankey.unknown', 'Unknown');

      if (cf.flowDirection === 'inbound') {
        if (!inboundFlows[counterparty]) inboundFlows[counterparty] = {};
        inboundFlows[counterparty][bank] = (inboundFlows[counterparty][bank] || 0) + cf.amount;
      } else {
        if (!outboundFlows[bank]) outboundFlows[bank] = {};
        outboundFlows[bank][counterparty] = (outboundFlows[bank][counterparty] || 0) + cf.amount;
      }
    }

    // Manual cashflows — use description as the "counterparty" name
    for (const cf of currencyManual) {
      const bank = getBankLabel(cf as any);
      const label = cf.description || t('cashflow.manual.title', 'Manual');

      if (cf.flowDirection === 'inbound') {
        if (!inboundFlows[label]) inboundFlows[label] = {};
        inboundFlows[label][bank] = (inboundFlows[label][bank] || 0) + cf.amount;
      } else {
        if (!outboundFlows[bank]) outboundFlows[bank] = {};
        outboundFlows[bank][label] = (outboundFlows[bank][label] || 0) + cf.amount;
      }
    }

    // Build nodes and links
    const nodeSet = new Set<string>();
    const links: Array<{ source: string; target: string; value: number }> = [];

    // Inbound links: counterparty (left) → bank account (middle)
    for (const [counterparty, banks] of Object.entries(inboundFlows)) {
      const srcName = `IN: ${counterparty}`;
      nodeSet.add(srcName);
      for (const [bank, amount] of Object.entries(banks)) {
        nodeSet.add(bank);
        links.push({ source: srcName, target: bank, value: amount });
      }
    }

    // Outbound links: bank account (middle) → counterparty (right)
    for (const [bank, counterparties] of Object.entries(outboundFlows)) {
      nodeSet.add(bank);
      for (const [counterparty, amount] of Object.entries(counterparties)) {
        const tgtName = `OUT: ${counterparty}`;
        nodeSet.add(tgtName);
        links.push({ source: bank, target: tgtName, value: amount });
      }
    }

    const nodes = Array.from(nodeSet).map(name => {
      let itemStyle: any = {};
      if (name.startsWith('IN: ')) {
        itemStyle = { color: '#4CAF50', borderColor: '#388E3C' };
      } else if (name.startsWith('OUT: ')) {
        itemStyle = { color: '#ff6b6b', borderColor: '#d32f2f' };
      } else {
        itemStyle = { color: '#64B5F6', borderColor: '#1976D2' };
      }
      return { name, itemStyle };
    });

    return { nodes, links };
  }, [cashflows, currency, manualCashflows, t]);

  if (sankeyData.nodes.length === 0 || sankeyData.links.length === 0) {
    return (
      <div style={{
        height: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
        backgroundColor: '#1e1e1e',
        borderRadius: 8,
      }}>
        {t('cashflow.messages.noData', 'No data available')} ({currency})
      </div>
    );
  }

  const option = {
    title: {
      text: title || `${t('cashflow.charts.sankey', 'Flow Routing')} (${currency})`,
      left: 'center',
      textStyle: { color: '#e0e0e0', fontSize: 14 },
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        if (params.dataType === 'edge') {
          // Link tooltip
          const src = params.data.source.replace(/^(IN|OUT): /, '');
          const tgt = params.data.target.replace(/^(IN|OUT): /, '');
          return `${src} → ${tgt}<br/><strong>${formatAmount(params.data.value)}</strong>`;
        }
        // Node tooltip
        const label = params.name.replace(/^(IN|OUT): /, '');
        return `<strong>${label}</strong><br/>${formatAmount(params.value || 0)}`;
      },
    },
    series: [{
      type: 'sankey',
      layout: 'none',
      emphasis: { focus: 'adjacency' },
      nodeAlign: 'left',
      draggable: false,
      top: 40,
      bottom: '3%',
      left: '20%',
      right: '20%',
      nodeWidth: 14,
      nodeGap: 10,
      data: sankeyData.nodes,
      links: sankeyData.links,
      label: {
        color: '#ccc',
        fontSize: 10,
        overflow: 'truncate',
        ellipsis: '…',
        width: 100,
        formatter: (params: any) => {
          // Strip the IN:/OUT: prefix and append formatted amount
          const label = params.name.replace(/^(IN|OUT): /, '');
          const amount = params.value != null ? formatAmount(params.value) : '';
          return amount ? `${label}\n${amount}` : label;
        },
      },
      lineStyle: {
        color: 'gradient',
        opacity: 0.35,
        curveness: 0.5,
      },
    }],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: height ?? 300 }}
      opts={{ renderer: 'canvas' }}
      notMerge={true}
    />
  );
};

export default CashflowBarChart;
